import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

var CATEGORIES = [
  { key: 'email', label: 'EMAIL TEMPLATES' },
  { key: 'sms', label: 'SMS TEMPLATES' },
  { key: 'notification', label: 'NOTIFICATIONS' },
  { key: 'disclaimer', label: 'DISCLAIMERS' },
]

// Job-type display labels + canonical add-form options. Free-text in the DB
// (the leaderboard match is case-insensitive substring), but we list the seed
// values so the picker is consistent.
var JOB_TYPE_LABELS = {
  install_aboveground: 'Above Install',
  install_inground: 'In-ground Install',
  liner_replacement: 'Liner Replacement',
  liner_inground: 'Liner Inground',
  opening: 'Opening',
  closing: 'Closing',
  service: 'Service',
  other: 'Other',
}

function jobTypeLabel(t) {
  if (!t) return 'Other'
  return JOB_TYPE_LABELS[t] || String(t).replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase() })
}

// Sort pool sizes by leading numeric value, falling back to lexical.
function sizeSortKey(s) {
  var raw = String(s || '').trim().toLowerCase()
  var m = raw.match(/(\d+(?:\.\d+)?)/)
  return m ? parseFloat(m[1]) : 1e9
}

// Reuse the LeaderboardPage matching rule so suggestions bucket the same way
// the scoreboard reads them.
function matchStandardForEntry(poolDim, jobType, standards) {
  if (!poolDim || !standards) return null
  var clean = String(poolDim).toLowerCase().replace(/\s+/g, '')
  var exact = standards.find(function(s) {
    return (!jobType || s.job_type === jobType) && String(s.pool_size).toLowerCase().replace(/\s+/g, '') === clean
  })
  if (exact) return exact
  var fuzzy = standards.find(function(s) {
    return (!jobType || s.job_type === jobType) && clean.indexOf(String(s.pool_size).toLowerCase().replace(/\s+/g, '')) !== -1
  })
  return fuzzy || null
}

export default function SettingsPage() {
  var { user, profile } = useAuth()
  var role = profile?.role
  var canEditStandards = role === 'owner' || role === 'office_staff'
  var [tab, setTab] = useState('templates')
  var [templates, setTemplates] = useState([])
  var [noteTemplates, setNoteTemplates] = useState([])
  var [appSettings, setAppSettings] = useState({})
  var [loading, setLoading] = useState(true)
  var [editingTpl, setEditingTpl] = useState(null)
  var [newNote, setNewNote] = useState({ title: '', body: '', business_id: '' })
  var [saving, setSaving] = useState(false)
  var [sandStandards, setSandStandards] = useState([])
  var [newStd, setNewStd] = useState({ pool_size: '', pool_shape: 'round', new_install_yards: '', replacement_yards: '', sand_type: 'mason sand', notes: '' })
  var [timeStandards, setTimeStandards] = useState([])
  var [timeEntries, setTimeEntries] = useState([])
  var [collapsedGroups, setCollapsedGroups] = useState({})
  var [newTimeStd, setNewTimeStd] = useState({ job_type: 'install_aboveground', pool_size: '', expected_hours: '' })
  var [newTimeStdError, setNewTimeStdError] = useState('')
  var [addingTimeStd, setAddingTimeStd] = useState(false)

  var fetchData = useCallback(async function() {
    setLoading(true)
    // 90-day window for the auto-suggest averages.
    var ninetyAgo = new Date(Date.now() - 90 * 86400000).toISOString()
    var [tRes, nRes, sRes, ssRes, tsRes, teRes] = await Promise.all([
      supabase.from('app_templates').select('*').order('category').order('title'),
      supabase.from('bid_note_templates').select('*').order('sort_order'),
      supabase.from('app_settings').select('*'),
      supabase.from('pool_sand_standards').select('*').order('pool_shape').order('new_install_yards'),
      supabase.from('pool_time_standards').select('*').order('job_type').order('pool_size'),
      supabase.from('job_time_entries')
        .select('hours_worked, jobs(job_type, pool_dimensions, status)')
        .not('clock_out', 'is', null)
        .gte('clock_in', ninetyAgo),
    ])
    setTemplates(tRes.data || [])
    setNoteTemplates(nRes.data || [])
    var settings = {}
    ;(sRes.data || []).forEach(function(s) { settings[s.key] = s.value })
    setAppSettings(settings)
    setSandStandards(ssRes.data || [])
    setTimeStandards(tsRes.data || [])
    setTimeEntries(teRes.data || [])
    setLoading(false)
  }, [])

  var updateSand = async function(id, field, value) {
    var v = (field === 'new_install_yards' || field === 'replacement_yards') ? (value === '' ? null : parseFloat(value)) : value
    setSandStandards(function(prev) { return prev.map(function(r) { return r.id === id ? Object.assign({}, r, { [field]: v }) : r }) })
    await supabase.from('pool_sand_standards').update({ [field]: v, updated_at: new Date().toISOString(), updated_by: user?.id || null }).eq('id', id)
  }

  var deleteSand = async function(id) {
    if (!window.confirm('Delete this sand standard?')) return
    await supabase.from('pool_sand_standards').delete().eq('id', id)
    setSandStandards(function(prev) { return prev.filter(function(r) { return r.id !== id }) })
  }

  // --- Time-standards handlers (CRUD on pool_time_standards) ---
  // saveTimeStdHours is called by the per-row debounced HoursCell. Returns
  // success/error so the cell can flash a check or red border.
  var saveTimeStdHours = useCallback(async function(id, hours) {
    var v = parseFloat(hours)
    if (isNaN(v) || v < 0) return { error: { message: 'Hours must be a non-negative number' } }
    setTimeStandards(function(prev) { return prev.map(function(r) { return r.id === id ? Object.assign({}, r, { expected_hours: v }) : r }) })
    var { error } = await supabase
      .from('pool_time_standards')
      .update({ expected_hours: v, updated_at: new Date().toISOString() })
      .eq('id', id)
    return { error: error || null }
  }, [])

  var deleteTimeStd = async function(id) {
    if (!window.confirm("Delete this standard? Past scoreboard data won't be affected.")) return
    var { error } = await supabase.from('pool_time_standards').delete().eq('id', id)
    if (error) { alert('Delete failed: ' + error.message); return }
    setTimeStandards(function(prev) { return prev.filter(function(r) { return r.id !== id }) })
  }

  var addTimeStd = async function() {
    setNewTimeStdError('')
    var jt = (newTimeStd.job_type || '').trim()
    var ps = (newTimeStd.pool_size || '').trim()
    var hrs = parseFloat(newTimeStd.expected_hours)
    if (!jt || !ps) { setNewTimeStdError('Job type and pool size are required.'); return }
    if (isNaN(hrs) || hrs < 0) { setNewTimeStdError('Hours must be a non-negative number.'); return }
    var dup = timeStandards.find(function(s) {
      return s.job_type === jt && String(s.pool_size).toLowerCase().trim() === ps.toLowerCase()
    })
    if (dup) { setNewTimeStdError('A standard for ' + jobTypeLabel(jt) + ' / ' + ps + ' already exists.'); return }
    setAddingTimeStd(true)
    var { data, error } = await supabase
      .from('pool_time_standards')
      .insert({ job_type: jt, pool_size: ps, expected_hours: hrs })
      .select()
      .single()
    setAddingTimeStd(false)
    if (error) { setNewTimeStdError('Add failed: ' + error.message); return }
    if (data) {
      setTimeStandards(function(prev) { return prev.concat([data]) })
      setNewTimeStd({ job_type: jt, pool_size: '', expected_hours: '' })
    }
  }

  var toggleGroup = function(jt) {
    setCollapsedGroups(function(prev) { return Object.assign({}, prev, { [jt]: !prev[jt] }) })
  }

  // Group + sort once per render. useMemo keeps this cheap and avoids the
  // useState+useEffect anti-pattern that caused flashing on other pages.
  var groupedStandards = useMemo(function() {
    var groups = {}
    timeStandards.forEach(function(s) {
      var key = s.job_type || 'other'
      if (!groups[key]) groups[key] = []
      groups[key].push(s)
    })
    Object.keys(groups).forEach(function(k) {
      groups[k].sort(function(a, b) {
        var ka = sizeSortKey(a.pool_size)
        var kb = sizeSortKey(b.pool_size)
        if (ka !== kb) return ka - kb
        return String(a.pool_size || '').localeCompare(String(b.pool_size || ''))
      })
    })
    var orderedKeys = Object.keys(groups).sort(function(a, b) { return jobTypeLabel(a).localeCompare(jobTypeLabel(b)) })
    return { groups: groups, keys: orderedKeys }
  }, [timeStandards])

  // Build per-standard suggestion from 90-day actuals. Only rows with ≥5
  // matching entries get a suggestion.
  var suggestionsById = useMemo(function() {
    if (!timeStandards.length || !timeEntries.length) return {}
    var byStdId = {}
    timeEntries.forEach(function(e) {
      var hours = parseFloat(e.hours_worked)
      if (!hours || hours <= 0) return
      var j = e.jobs
      if (!j) return
      var std = matchStandardForEntry(j.pool_dimensions, j.job_type, timeStandards)
      if (!std) return
      if (!byStdId[std.id]) byStdId[std.id] = []
      byStdId[std.id].push(hours)
    })
    var out = {}
    Object.keys(byStdId).forEach(function(id) {
      var arr = byStdId[id]
      if (arr.length < 5) return
      var sum = arr.reduce(function(s, n) { return s + n }, 0)
      out[id] = { avg: Math.round((sum / arr.length) * 4) / 4, count: arr.length }
    })
    return out
  }, [timeEntries, timeStandards])

  // Existing pool-size strings for the add-row autocomplete datalist.
  var existingSizes = useMemo(function() {
    var seen = {}
    timeStandards.forEach(function(s) { if (s.pool_size) seen[s.pool_size] = true })
    return Object.keys(seen).sort(function(a, b) { return sizeSortKey(a) - sizeSortKey(b) })
  }, [timeStandards])

  var addSand = async function() {
    if (!newStd.pool_size || !newStd.new_install_yards) return
    var row = {
      pool_size: newStd.pool_size,
      pool_shape: newStd.pool_shape || null,
      new_install_yards: parseFloat(newStd.new_install_yards),
      replacement_yards: newStd.replacement_yards !== '' ? parseFloat(newStd.replacement_yards) : parseFloat(newStd.new_install_yards) / 2,
      sand_type: newStd.sand_type || 'mason sand',
      notes: newStd.notes || null,
      updated_by: user?.id || null,
    }
    var { data, error } = await supabase.from('pool_sand_standards').insert(row).select().single()
    if (error) { alert('Add failed: ' + error.message); return }
    if (data) { setSandStandards(function(prev) { return prev.concat([data]) }); setNewStd({ pool_size: '', pool_shape: 'round', new_install_yards: '', replacement_yards: '', sand_type: 'mason sand', notes: '' }) }
  }

  useEffect(function() { fetchData() }, [fetchData])

  var saveSetting = async function(key, value) {
    setAppSettings(function(prev) { return Object.assign({}, prev, { [key]: value }) })
    await supabase.from('app_settings').upsert({ key: key, value: value }, { onConflict: 'key' })
  }

  var saveTemplate = async function(tpl) {
    setSaving(true)
    await supabase.from('app_templates').update({ body: tpl.body, updated_at: new Date().toISOString(), updated_by: user.id }).eq('id', tpl.id)
    setTemplates(function(prev) { return prev.map(function(t) { return t.id === tpl.id ? Object.assign({}, t, { body: tpl.body }) : t }) })
    setEditingTpl(null)
    setSaving(false)
  }

  var addNoteTemplate = async function() {
    if (!newNote.title || !newNote.body) return
    var { data } = await supabase.from('bid_note_templates').insert({ title: newNote.title, body: newNote.body, business_id: newNote.business_id || null, sort_order: noteTemplates.length }).select().single()
    if (data) { setNoteTemplates(function(prev) { return [...prev, data] }); setNewNote({ title: '', body: '', business_id: '' }) }
  }

  var deleteNoteTemplate = async function(id) {
    if (!window.confirm('Delete this note template?')) return
    await supabase.from('bid_note_templates').delete().eq('id', id)
    setNoteTemplates(function(prev) { return prev.filter(function(t) { return t.id !== id }) })
  }

  var updateNoteTemplate = async function(id, field, value) {
    setNoteTemplates(function(prev) { return prev.map(function(t) { return t.id === id ? Object.assign({}, t, { [field]: value }) : t }) })
    await supabase.from('bid_note_templates').update({ [field]: value }).eq('id', id)
  }

  if (loading) return <div style={st.loading}>Loading settings...</div>

  return (
    <div style={st.container}>
      <div style={st.header}><div style={st.title}>SETTINGS</div><div style={st.sub}>Manage templates, defaults, and configuration</div></div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          ['templates', 'TEMPLATES'],
          ['notes', 'BID NOTES'],
          ['sand', 'SAND STANDARDS'],
          canEditStandards ? ['time', 'TIME STANDARDS'] : null,
          ['config', 'CONFIGURATION'],
        ].filter(Boolean).map(function(t) {
          return <button key={t[0]} onClick={function() { setTab(t[0]) }} style={{ ...st.tabBtn, ...(tab === t[0] ? st.tabActive : {}) }}>{t[1]}</button>
        })}
      </div>

      {/* === TEMPLATES TAB === */}
      {tab === 'templates' && (
        <div>
          {CATEGORIES.map(function(cat) {
            var catTemplates = templates.filter(function(t) { return t.category === cat.key })
            if (catTemplates.length === 0) return null
            return (
              <div key={cat.key} style={{ marginBottom: '28px' }}>
                <div style={st.sectionTitle}>{cat.label}</div>
                {catTemplates.map(function(tpl) {
                  var isEditing = editingTpl && editingTpl.id === tpl.id
                  return (
                    <div key={tpl.id} style={st.tplCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={function() { setEditingTpl(isEditing ? null : Object.assign({}, tpl)) }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{tpl.title}</div>
                          {tpl.description && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{tpl.description}</div>}
                          {tpl.business_id && <span style={{ fontSize: '10px', color: tpl.business_id === 'pools' ? '#b48c3c' : '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' }}>{tpl.business_id === 'pools' ? 'POOLS' : 'RIVERBEND'}</span>}
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>{isEditing ? 'EDITING' : 'CLICK TO EDIT'}</div>
                      </div>
                      {isEditing && (
                        <div style={{ marginTop: '12px' }}>
                          {tpl.variables && tpl.variables.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ fontSize: '9px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '2px', color: 'rgba(255,255,255,0.25)', marginBottom: '4px' }}>AVAILABLE VARIABLES</div>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {tpl.variables.map(function(v) { return <button key={v} onClick={function() { setEditingTpl(Object.assign({}, editingTpl, { body: (editingTpl.body || '') + '{' + v + '}' })) }} style={{ padding: '3px 8px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '3px', color: '#b48c3c', fontSize: '11px', cursor: 'pointer', fontFamily: "'Barlow Condensed',sans-serif" }}>{'{'}{v}{'}'}</button> })}
                              </div>
                            </div>
                          )}
                          <textarea value={editingTpl.body || ''} onChange={function(e) { setEditingTpl(Object.assign({}, editingTpl, { body: e.target.value })) }} style={st.textarea} rows={8} />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button onClick={function() { setEditingTpl(null) }} style={st.cancelBtn}>CANCEL</button>
                            <button disabled={saving} onClick={function() { saveTemplate(editingTpl) }} style={st.saveBtn}>{saving ? 'SAVING...' : 'SAVE TEMPLATE'}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      )}

      {/* === BID NOTES TAB === */}
      {tab === 'notes' && (
        <div>
          <div style={st.sectionTitle}>BID NOTE QUICK TEMPLATES</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginBottom: '16px' }}>These appear as clickable chips on the Bids page above the notes field.</div>
          {noteTemplates.map(function(nt) {
            return (
              <div key={nt.id} style={st.tplCard}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <input value={nt.title} onChange={function(e) { updateNoteTemplate(nt.id, 'title', e.target.value) }} style={{ ...st.input, fontWeight: 600, marginBottom: '6px' }} />
                    <textarea value={nt.body} onChange={function(e) { updateNoteTemplate(nt.id, 'body', e.target.value) }} style={{ ...st.textarea, minHeight: '60px' }} />
                  </div>
                  <button onClick={function() { deleteNoteTemplate(nt.id) }} style={{ padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', color: '#e05c5c', fontSize: '11px', cursor: 'pointer' }}>DEL</button>
                </div>
              </div>
            )
          })}
          <div style={{ ...st.tplCard, borderColor: 'rgba(180,140,60,0.2)' }}>
            <div style={{ fontSize: '11px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1.5px', color: '#b48c3c', marginBottom: '8px' }}>ADD NEW TEMPLATE</div>
            <input value={newNote.title} onChange={function(e) { setNewNote(Object.assign({}, newNote, { title: e.target.value })) }} style={{ ...st.input, marginBottom: '6px' }} placeholder="Template title" />
            <textarea value={newNote.body} onChange={function(e) { setNewNote(Object.assign({}, newNote, { body: e.target.value })) }} style={{ ...st.textarea, minHeight: '60px' }} placeholder="Template body text..." />
            <button onClick={addNoteTemplate} style={{ ...st.saveBtn, marginTop: '8px' }}>+ ADD NOTE TEMPLATE</button>
          </div>
        </div>
      )}

      {/* === SAND STANDARDS TAB === */}
      {tab === 'sand' && (
        <div>
          <div style={st.sectionTitle}>POOL SAND STANDARDS</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginBottom: '16px' }}>Default sand amounts per pool size. Replacements use roughly half since some base is already in place. Used by the schedule confirmation email and crew job cards.</div>
          <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>POOL SIZE</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>SHAPE</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>NEW (YD)</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>REPLACE (YD)</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>SAND TYPE</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>NOTES</th>
                  <th style={{ width: '60px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}></th>
                </tr>
              </thead>
              <tbody>
                {sandStandards.map(function(r) {
                  return (
                    <tr key={r.id}>
                      <td style={{ padding: '4px 6px' }}><input defaultValue={r.pool_size} onBlur={function(e) { if (e.target.value !== r.pool_size) updateSand(r.id, 'pool_size', e.target.value) }} style={st.tdInput} /></td>
                      <td style={{ padding: '4px 6px' }}>
                        <select defaultValue={r.pool_shape || ''} onChange={function(e) { updateSand(r.id, 'pool_shape', e.target.value) }} style={st.tdInput}>
                          <option value="round">round</option>
                          <option value="oval">oval</option>
                          <option value="rectangle">rectangle</option>
                          <option value="kidney">kidney</option>
                        </select>
                      </td>
                      <td style={{ padding: '4px 6px' }}><input type="number" step="0.25" min="0" defaultValue={r.new_install_yards} onBlur={function(e) { if (e.target.value !== String(r.new_install_yards)) updateSand(r.id, 'new_install_yards', e.target.value) }} style={{ ...st.tdInput, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px' }}><input type="number" step="0.25" min="0" defaultValue={r.replacement_yards} onBlur={function(e) { if (e.target.value !== String(r.replacement_yards)) updateSand(r.id, 'replacement_yards', e.target.value) }} style={{ ...st.tdInput, textAlign: 'right' }} /></td>
                      <td style={{ padding: '4px 6px' }}><input defaultValue={r.sand_type || 'mason sand'} onBlur={function(e) { if (e.target.value !== r.sand_type) updateSand(r.id, 'sand_type', e.target.value) }} style={st.tdInput} /></td>
                      <td style={{ padding: '4px 6px' }}><input defaultValue={r.notes || ''} onBlur={function(e) { if ((e.target.value || null) !== r.notes) updateSand(r.id, 'notes', e.target.value || null) }} style={st.tdInput} /></td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}><button onClick={function() { deleteSand(r.id) }} style={{ padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', color: '#e05c5c', fontSize: '11px', cursor: 'pointer' }}>DEL</button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ ...st.tplCard, borderColor: 'rgba(180,140,60,0.2)' }}>
            <div style={{ fontSize: '11px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1.5px', color: '#b48c3c', marginBottom: '8px' }}>ADD POOL SIZE</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr) auto', gap: '6px', alignItems: 'end' }}>
              <input placeholder="e.g. 24' Round" value={newStd.pool_size} onChange={function(e) { setNewStd(Object.assign({}, newStd, { pool_size: e.target.value })) }} style={st.input} />
              <select value={newStd.pool_shape} onChange={function(e) { setNewStd(Object.assign({}, newStd, { pool_shape: e.target.value })) }} style={st.input}>
                <option value="round">round</option>
                <option value="oval">oval</option>
                <option value="rectangle">rectangle</option>
                <option value="kidney">kidney</option>
              </select>
              <input type="number" step="0.25" min="0" placeholder="New yd" value={newStd.new_install_yards} onChange={function(e) { setNewStd(Object.assign({}, newStd, { new_install_yards: e.target.value })) }} style={st.input} />
              <input type="number" step="0.25" min="0" placeholder="Replace yd (auto = half)" value={newStd.replacement_yards} onChange={function(e) { setNewStd(Object.assign({}, newStd, { replacement_yards: e.target.value })) }} style={st.input} />
              <input placeholder="sand type" value={newStd.sand_type} onChange={function(e) { setNewStd(Object.assign({}, newStd, { sand_type: e.target.value })) }} style={st.input} />
              <input placeholder="notes" value={newStd.notes} onChange={function(e) { setNewStd(Object.assign({}, newStd, { notes: e.target.value })) }} style={st.input} />
              <button onClick={addSand} style={st.saveBtn}>+ ADD</button>
            </div>
          </div>
        </div>
      )}

      {/* === TIME STANDARDS TAB === */}
      {tab === 'time' && canEditStandards && (
        <div>
          <div style={st.sectionTitle}>TIME STANDARDS — EXPECTED HOURS PER JOB</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginBottom: '16px' }}>
            Drives the crew scoreboard's actual-vs-expected comparison. Tap a number to edit; saves automatically 800ms after you stop typing.
          </div>

          {groupedStandards.keys.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px dashed rgba(255,255,255,0.08)' }}>No time standards yet — add one below.</div>
          )}

          {groupedStandards.keys.map(function(jt) {
            var rows = groupedStandards.groups[jt]
            var collapsed = !!collapsedGroups[jt]
            return (
              <div key={jt} style={{ marginBottom: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={function() { toggleGroup(jt) }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px' }}
                >
                  <span>{jobTypeLabel(jt)} <span style={{ color: 'rgba(255,255,255,0.3)', marginLeft: '6px', fontWeight: 400 }}>{rows.length}</span></span>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>{collapsed ? '+' : String.fromCodePoint(0x2212)}</span>
                </button>
                {!collapsed && (
                  <div>
                    {rows.map(function(r) {
                      var sug = suggestionsById[r.id]
                      return (
                        <TimeStdRow
                          key={r.id}
                          row={r}
                          suggestion={sug}
                          onSave={saveTimeStdHours}
                          onDelete={function() { deleteTimeStd(r.id) }}
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}

          {/* Add new combination */}
          <div style={{ marginTop: '20px', padding: '14px', backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '4px' }}>
            <div style={{ fontSize: '11px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1.5px', color: '#b48c3c', marginBottom: '10px' }}>+ ADD NEW COMBINATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1.2fr) 100px auto', gap: '8px', alignItems: 'end' }}>
              <div>
                <div style={{ fontSize: '9px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' }}>JOB TYPE</div>
                <select
                  value={newTimeStd.job_type}
                  onChange={function(e) { setNewTimeStd(Object.assign({}, newTimeStd, { job_type: e.target.value })); setNewTimeStdError('') }}
                  style={st.input}
                >
                  {Object.keys(JOB_TYPE_LABELS).map(function(k) { return <option key={k} value={k}>{JOB_TYPE_LABELS[k]}</option> })}
                </select>
              </div>
              <div>
                <div style={{ fontSize: '9px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' }}>POOL SIZE</div>
                <input
                  list="time-std-sizes"
                  value={newTimeStd.pool_size}
                  onChange={function(e) { setNewTimeStd(Object.assign({}, newTimeStd, { pool_size: e.target.value })); setNewTimeStdError('') }}
                  placeholder="e.g. 24 round, 18x33 oval, Any"
                  style={st.input}
                />
                <datalist id="time-std-sizes">
                  {existingSizes.map(function(s) { return <option key={s} value={s} /> })}
                  <option value="Any" />
                </datalist>
              </div>
              <div>
                <div style={{ fontSize: '9px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '2px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' }}>HOURS</div>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={newTimeStd.expected_hours}
                  onChange={function(e) { setNewTimeStd(Object.assign({}, newTimeStd, { expected_hours: e.target.value })); setNewTimeStdError('') }}
                  placeholder="0"
                  style={Object.assign({}, st.input, { textAlign: 'right' })}
                />
              </div>
              <button type="button" disabled={addingTimeStd} onClick={addTimeStd} style={Object.assign({}, st.saveBtn, { opacity: addingTimeStd ? 0.6 : 1 })}>{addingTimeStd ? 'ADDING…' : '+ ADD'}</button>
            </div>
            {newTimeStdError && <div style={{ marginTop: '8px', fontSize: '12px', color: '#e05c5c' }}>{newTimeStdError}</div>}
          </div>
        </div>
      )}

      {/* === CONFIGURATION TAB === */}
      {tab === 'config' && (
        <div>
          <div style={st.sectionTitle}>COMPANY DEFAULTS</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
            <SettingField label="SHOP ADDRESS" settingKey="shop_address" value={appSettings.shop_address || '11260 Dunham Rd, Hartland MI 48353'} onSave={saveSetting} />
            <SettingField label="OWNER PHONE" settingKey="owner_phone" value={appSettings.owner_phone || '(810) 923-6229'} onSave={saveSetting} />
            <SettingField label="LUNCH STOP DURATION (MIN)" settingKey="lunch_duration" value={appSettings.lunch_duration || '30'} onSave={saveSetting} type="number" />
            <SettingField label="RAIN DAY DEFAULT (DAYS)" settingKey="RAIN_DAY_DEFAULT_BUMP_DAYS" value={appSettings.RAIN_DAY_DEFAULT_BUMP_DAYS || '1'} onSave={saveSetting} type="number" />
            <SettingField label="FOLLOW-UP REMINDER (DAYS)" settingKey="followup_days" value={appSettings.followup_days || '7'} onSave={saveSetting} type="number" />
          </div>

          <div style={st.sectionTitle}>QUICK LINKS</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <a href="/pricing" style={st.linkBtn}>POOL PRICING</a>
            <a href="/pricing" style={st.linkBtn}>TIME STANDARDS</a>
            <a href="/store-requests" style={st.linkBtn}>STORE REQUESTS</a>
          </div>
        </div>
      )}
    </div>
  )
}

// One row in the TIME STANDARDS list. Owns the input value locally so a
// keystroke doesn't ripple through the whole table; debounces the save by
// 800ms and flashes a green check or red border based on result. Saving
// pressing Enter or blurring the input commits immediately.
function TimeStdRow({ row, suggestion, onSave, onDelete }) {
  var [val, setVal] = useState(row.expected_hours == null ? '' : String(row.expected_hours))
  var [state, setState] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'
  var [errMsg, setErrMsg] = useState('')
  var timerRef = useRef(null)
  var savedTimerRef = useRef(null)

  // If the underlying row changes from elsewhere (suggestion-fill, refetch),
  // sync the displayed value — but only when no edit is in flight.
  useEffect(function() {
    if (state === 'idle') {
      var canonical = row.expected_hours == null ? '' : String(row.expected_hours)
      if (canonical !== val) setVal(canonical)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.expected_hours])

  useEffect(function() {
    return function() {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  var commit = useCallback(async function(raw) {
    var trimmed = String(raw == null ? '' : raw).trim()
    if (trimmed === '' || trimmed === String(row.expected_hours)) {
      setState('idle')
      return
    }
    setState('saving')
    setErrMsg('')
    var res = await onSave(row.id, trimmed)
    if (res && res.error) {
      setState('error')
      setErrMsg(res.error.message || 'Save failed')
    } else {
      setState('saved')
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      savedTimerRef.current = setTimeout(function() { setState('idle') }, 2000)
    }
  }, [onSave, row.id, row.expected_hours])

  var onChange = function(e) {
    var v = e.target.value
    setVal(v)
    if (state === 'error') setState('idle')
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(function() { commit(v) }, 800)
  }

  var onBlur = function() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    commit(val)
  }

  var onKeyDown = function(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
      commit(val)
    }
  }

  var inputBorder = state === 'error' ? '1px solid rgba(224,92,92,0.6)' : '1px solid rgba(255,255,255,0.1)'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderTop: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 200px', fontSize: '13px', color: '#ffffff', fontWeight: 500, minWidth: 0 }}>{row.pool_size || '—'}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="number"
          step="0.25"
          min="0"
          value={val}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          style={{ width: '88px', padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.04)', border: inputBorder, borderRadius: '3px', color: '#b48c3c', fontSize: '14px', fontWeight: 700, textAlign: 'right', outline: 'none', fontFamily: "'Barlow',sans-serif" }}
        />
        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' }}>HRS</span>
        <span style={{ width: '60px', fontSize: '11px', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' }}>
          {state === 'saving' && <span style={{ color: 'rgba(255,255,255,0.4)' }}>SAVING…</span>}
          {state === 'saved' && <span style={{ color: '#4a8c6a' }}>{String.fromCodePoint(0x2713)} SAVED</span>}
          {state === 'error' && <span style={{ color: '#e05c5c' }} title={errMsg}>FAILED</span>}
        </span>
      </div>
      {suggestion && (
        <button
          type="button"
          onClick={function() { setVal(String(suggestion.avg)); commit(String(suggestion.avg)) }}
          title={'Average of last ' + suggestion.count + ' completed jobs (90-day window). Tap to apply.'}
          style={{ padding: '4px 10px', backgroundColor: 'rgba(74,140,106,0.1)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', color: '#4a8c6a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' }}
        >
          SUGGESTED: {suggestion.avg} ({suggestion.count})
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete standard"
        style={{ padding: '4px 10px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', color: '#e05c5c', fontSize: '12px', cursor: 'pointer', marginLeft: 'auto' }}
      >
        {String.fromCodePoint(0x1F5D1, 0xFE0F)}
      </button>
    </div>
  )
}

function SettingField({ label, settingKey, value, onSave, type }) {
  var [val, setVal] = useState(value)
  var [saved, setSaved] = useState(false)
  return (
    <div>
      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' }}>{label}</div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input type={type || 'text'} value={val} onChange={function(e) { setVal(e.target.value); setSaved(false) }} style={{ flex: 1, padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
        <button onClick={function() { onSave(settingKey, val); setSaved(true); setTimeout(function() { setSaved(false) }, 2000) }} style={{ padding: '10px 14px', backgroundColor: saved ? '#4a8c6a' : '#b48c3c', border: 'none', borderRadius: '3px', color: saved ? '#fff' : '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' }}>{saved ? 'SAVED' : 'SAVE'}</button>
      </div>
    </div>
  )
}

var st = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '900px' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' },
  header: { marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  sub: { fontSize: '14px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  tabBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer' },
  tabActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  sectionTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c', marginBottom: '12px' },
  tplCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '16px', marginBottom: '6px' },
  textarea: { width: '100%', padding: '12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
  tdInput: { width: '100%', padding: '6px 8px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
  cancelBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  linkBtn: { padding: '10px 16px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', textDecoration: 'none' },
}
